import type { BoardBox } from "@/shelf/types/types";

/**
 * Which way a rack faces: one of the four quarter turns.
 *
 * Every quarter, because which way reads correctly depends on the artwork: a
 * spine printed top to bottom — the usual way — lands upside down when its rack
 * is turned clockwise, and reads correctly the other way round.
 */
export type ShelfRotationDegrees = 0 | 90 | 180 | 270;

/** A rack nobody has turned. */
export const UPRIGHT_ROTATION_DEGREES = 0;

const SHELF_ROTATIONS: readonly ShelfRotationDegrees[] = [0, 90, 180, 270];

/**
 * The quarter turn a stored angle means. An angle written by something else is
 * held to the nearest quarter, and no angle at all is upright — so a Shelf from
 * a newer desktop can never draw a rack at an angle this board cannot lay out.
 */
export const normalizeShelfRotation = (
  rotationDegrees: number | null | undefined,
): ShelfRotationDegrees => {
  if (
    rotationDegrees === undefined ||
    rotationDegrees === null ||
    !Number.isFinite(rotationDegrees)
  ) {
    return UPRIGHT_ROTATION_DEGREES;
  }
  const wrapped = ((rotationDegrees % 360) + 360) % 360;
  const quarters = Math.round(wrapped / 90) % 4;
  return SHELF_ROTATIONS[quarters] ?? UPRIGHT_ROTATION_DEGREES;
};

/**
 * How far the rack's own geometry — discs standing side by side — is turned to
 * draw a rack stored at 0.
 *
 * 0 is the rack as the desktop first puts it down: the cases lying one above
 * another with their spines reading left to right, because a spine printed top
 * to bottom reads correctly when its case is laid flat. Held to the desktop's
 * number deliberately — a rack drawn at another angle here than where it was
 * built is a different rack.
 */
const RESTING_DRAWN_ROTATION_DEGREES = 270;

/** The angle a rack is drawn at, from the angle it is stored at. */
export const resolveDrawnShelfRotation = (rotationDegrees: number | null | undefined) =>
  normalizeShelfRotation(normalizeShelfRotation(rotationDegrees) + RESTING_DRAWN_ROTATION_DEGREES);

/** Whether a rack lies on its side, so it is as tall as it was wide. */
export const isShelfOnItsSide = (rotationDegrees: ShelfRotationDegrees) =>
  rotationDegrees % 180 !== 0;

/**
 * The space a turned rack actually takes up, which is what "fit everything on
 * screen" is measured against. A rack on its side is as tall as it was wide.
 */
export const resolveRotatedBounds = ({
  box,
  centreXMillimeters,
  centreYMillimeters,
  rotationDegrees,
}: {
  box: BoardBox;
  centreXMillimeters: number;
  centreYMillimeters: number;
  rotationDegrees: ShelfRotationDegrees;
}): BoardBox => {
  if (!isShelfOnItsSide(rotationDegrees)) {
    return box;
  }
  return {
    xMillimeters: centreXMillimeters - box.heightMillimeters / 2,
    yMillimeters: centreYMillimeters - box.widthMillimeters / 2,
    widthMillimeters: box.heightMillimeters,
    heightMillimeters: box.widthMillimeters,
  };
};
